import { Link } from "react-router-dom";
import { ArrowRight, Languages, MonitorSmartphone } from "lucide-react";
import { motion } from "framer-motion";
import { usePageMeta } from "../hooks/usePageMeta";
import { PageHeader } from "../components/PageHeader";
import { CtaBanner } from "../components/CtaBanner";
import { fadeUp, stagger } from "../lib/motion";

const AREE = [
  {
    icon: Languages,
    titolo: "Certificazioni linguistiche",
    testo:
      "Percorsi per certificare le competenze linguistiche, utili per lo studio, il lavoro e la mobilità internazionale. Ti orientiamo verso la certificazione più adatta al tuo livello e ai tuoi obiettivi.",
    testId: "cert-area-linguistiche",
  },
  {
    icon: MonitorSmartphone,
    titolo: "Certificazioni informatiche",
    testo:
      "Percorsi per certificare le competenze digitali e informatiche, sempre più richieste in ambito scolastico, universitario e professionale. Ti accompagniamo nella scelta e nella preparazione.",
    testId: "cert-area-informatiche",
  },
];

export default function Certificazioni() {
  usePageMeta(
    "Certificazioni",
    "Certificazioni linguistiche e informatiche: percorsi per certificare le tue competenze con l'orientamento di Centro Studi Forma Futuro."
  );

  return (
    <main>
      <PageHeader
        testId="page-certificazioni"
        eyebrow="Percorsi · Certificazioni"
        title="Dai valore alle tue competenze."
        description="Linguistiche e informatiche: le certificazioni attestano ciò che sai fare e rafforzano il tuo profilo, nello studio e nel lavoro."
      />
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="grid grid-cols-1 md:grid-cols-2 gap-7 max-w-5xl"
          >
            {AREE.map((a) => (
              <motion.div
                key={a.titolo}
                variants={fadeUp}
                data-testid={a.testId}
                className="group bg-white rounded-[1.6rem] border border-slate-200 p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:border-azure hover:shadow-[0_28px_60px_-30px_rgba(8,42,89,0.35)]"
              >
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-azure-light text-azure mb-7 transition-colors duration-300 group-hover:bg-azure group-hover:text-white">
                  <a.icon size={26} strokeWidth={1.8} />
                </span>
                <h2 className="text-xl lg:text-2xl font-extrabold tracking-tight text-navy">
                  {a.titolo}
                </h2>
                <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                  {a.testo}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-12 max-w-5xl">
            <div className="bg-azure-light border border-azure-soft rounded-[1.6rem] px-8 py-8 lg:px-10 flex flex-col sm:flex-row sm:items-center gap-6">
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed flex-1">
                Vuoi sapere quali certificazioni sono disponibili e quale fa al
                caso tuo? Contattaci: ti forniamo tutte le informazioni in un
                colloquio di orientamento.
              </p>
              <Link
                to="/contatti"
                data-testid="certificazioni-cta"
                className="inline-flex items-center justify-center gap-2.5 rounded-full bg-azure px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#1C446E] hover:-translate-y-0.5 shrink-0 group"
              >
                Richiedi informazioni
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <CtaBanner
        title="Certifica le tue competenze."
        text="Scrivici i tuoi obiettivi: ti indichiamo il percorso di certificazione più adatto."
      />
    </main>
  );
}
